/**
 * @fileoverview The one read of "What to plant" (D67): the server's list of what can go in now around the garden, for the
 * garden being tended (the owner's place, plan and plants for a member).
 *
 * Where: WhatToPlantPage.
 * API: GET /plants/what-to-plant?lang= (api/whatToPlant).
 * Caching: the key holds the garden, its own day and the language, so a new day or language reads again; a plant added elsewhere
 * shows after the stale time. Not read until the garden's place
 * (and so its day) is known, so the first visit makes one call, not two: the server's first call for a place can be slow.
 *
 * @module useWhatToPlant
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/hooks/useAuth';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getWhatToPlant, type WhatToPlant } from '@/shared/api/whatToPlant';
import { chosenGarden } from '@/shared/api/gardenMembers';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';

/** The first word of the list's React Query key. */
export const WHAT_TO_PLANT_QUERY_KEY = 'what-to-plant';

/** What the page is handed. */
export interface WhatToPlantReading {
  /** The server's answer, or undefined until it is in. */
  data: WhatToPlant | undefined;
  /** True while the first read is on its way. */
  isLoading: boolean;
  /** The failure, or null. */
  error: unknown;
}

/**
 * Reads the what-to-plant list for the garden being tended.
 *
 * @returns The answer, whether it is loading, and any failure.
 */
export function useWhatToPlant(): WhatToPlantReading {
  const { mysqlUser } = useAuth();
  const { i18n: { language } } = useTranslation();
  const { date, known } = useGardenPostcode();
  const gardenId = chosenGarden() ?? mysqlUser?.user_id ?? null;
  const { data, isLoading, error } = useQuery({
    queryKey: [WHAT_TO_PLANT_QUERY_KEY, gardenId, date, language],
    queryFn: () => getWhatToPlant(language),
    enabled: Boolean(mysqlUser) && known,
    staleTime: QUERY_STALE_TIME_MS,
  });
  return { data, isLoading: isLoading || !known, error: error ?? null };
}
